import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import {
  FriendMatchupItem as CoreFriend,
  Props as FriendProps,
} from '@sorare/core/src/components/rivals/FriendMatchupItem';
import { RivalsAvatar } from '@sorare/core/src/components/rivals/RivalsAvatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { rivals } from '@sorare/core/src/lib/glossary';

import { Friend_footballRivalsManager } from './__generated__/index.graphql';

type Props = {
  manager: Friend_footballRivalsManager;
  composed?: boolean;
  status: FriendProps['status'];
  onAcceptInvite?: () => void;
  onRejectInvite?: () => void;
  onChallenge?: () => void;
  hideAccept?: boolean;
  cta?: ReactNode;
};
export const Friend = ({
  manager,
  composed,
  status,
  onAcceptInvite,
  onRejectInvite,
  onChallenge,
  hideAccept,
  cta,
}: Props) => {
  const [updatedStatus, setUpdatedStatus] = useState<
    FriendProps['status'] | null
  >(null);

  const onClick: FriendProps['onClick'] = action => {
    if (action === 'reject' && updatedStatus !== 'refused') {
      setUpdatedStatus('refused');
      onRejectInvite?.();
    }
    if (action === 'accept' && updatedStatus !== 'accepted') {
      setUpdatedStatus('accepted');
      onAcceptInvite?.();
    }
    if (action === 'challenge' && updatedStatus !== 'created_by_me') {
      setUpdatedStatus('created_by_me');
      onChallenge?.();
    }
  };

  return (
    <CoreFriend
      content={<RivalsAvatar manager={manager} />}
      extra={
        <Vertical gap={0} center>
          <LabelS
            as="span"
            color={composed ? 'var(--c-green-800)' : 'var(--c-neutral-600)'}
          >
            {composed ? (
              <FormattedMessage {...rivals.lineupSet} />
            ) : (
              <FormattedMessage
                id="rivals.matchups.Friend.noLineup"
                defaultMessage="No Lineup"
              />
            )}
          </LabelS>
          <LabelS as="p" color="var(--c-neutral-500)">
            <FormattedMessage
              id="rivals.Friend.rivalryRecords"
              defaultMessage="({wins}-{losses})"
              values={{
                wins: manager?.myRivalry?.records.wins,
                losses: manager?.myRivalry?.records.losses,
              }}
            />
          </LabelS>
        </Vertical>
      }
      name={<MyClubLink user={manager.user}>{manager.nickname}</MyClubLink>}
      status={updatedStatus || status}
      onClick={onClick}
      hideAccept={hideAccept}
      cta={cta}
    />
  );
};

Friend.fragments = {
  manager: gql`
    fragment Friend_footballRivalsManager on FootballRivalsManager {
      id
      nickname
      myRivalry {
        id
        records {
          wins
          losses
        }
      }
      user {
        slug
        ...MyClubLink_publicUserInfoInterface
      }
      ...RivalsAvatar_footballRivalsManager
    }
    ${RivalsAvatar.fragments.footballRivalsManager}
    ${MyClubLink.fragments.user}
  ` as TypedDocumentNode<Friend_footballRivalsManager>,
};
